import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';

const MentorProfile = () => {
  const { mentorName } = useParams();
  const [mentor, setMentor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMentor = async () => {
      try {
        const response = await fetch(`/api/mentors/fetchMentorByName?name=${encodeURIComponent(mentorName)}`);
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || 'Failed to fetch mentor');
        }
        
        setMentor(data.mentor);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (mentorName) {
      fetchMentor();
    }
  }, [mentorName]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200"
        >
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 border-4 border-[#02B491] border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading Mentor Profile</h2>
          <p className="text-lg text-gray-600 max-w-md">
            Please wait while we load the mentor information.
          </p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center p-8 sm:p-12 bg-white rounded-xl shadow-lg border border-gray-200 max-w-2xl mx-4"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Mentor Not Found</h2>
          <p className="text-lg text-gray-600 mb-6 leading-relaxed">
            The mentor profile you're looking for could not be found or may not be available.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-[#02B491] text-white rounded-lg hover:bg-[#029078] transition-colors font-medium"
          >
            Go Back
          </button>
        </motion.div>
      </div>
    );
  }

  if (!mentor) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="w-full bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center md:text-left md:flex md:items-center md:gap-8"
          >
            {/* Profile Picture */}
            <div className="flex justify-center md:justify-start mb-6 md:mb-0">
              {mentor.picture ? (
                <motion.img
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  src={mentor.picture.url}
                  alt={mentor.name}
                  className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-white shadow-xl"
                />
              ) : (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-[#02B491] to-[#029078] flex items-center justify-center shadow-xl"
                >
                  <span className="text-white text-4xl md:text-5xl font-bold">
                    {mentor.name?.charAt(0)?.toUpperCase()}
                  </span>
                </motion.div>
              )}
            </div>

            {/* Basic Info */}
            <div className="flex-grow">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl md:text-5xl font-bold text-gray-900 mb-2"
              >
                {mentor.name}
              </motion.h1>
              
              {mentor.currentRole && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-xl md:text-2xl text-gray-700 mb-2 font-semibold"
                >
                  {mentor.currentRole}
                </motion.p>
              )}
              
              {mentor.currentSector && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-lg text-gray-600 mb-6"
                >
                  {mentor.currentSector}
                </motion.p>
              )}

              {/* Contact Actions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start"
              >
                {mentor.linkedinProfile && (
                  <a
                    href={mentor.linkedinProfile.startsWith('http') ? mentor.linkedinProfile : `https://${mentor.linkedinProfile}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-3 px-6 py-3 bg-[#0077B5] text-white rounded-lg hover:bg-[#005885] transition-all duration-200 font-semibold shadow-lg hover:shadow-xl group"
                  >
                    <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    Connect on LinkedIn
                  </a>
                )}
                
                {mentor.email && (
                  <a
                    href={`mailto:${mentor.email}`}
                    className="inline-flex items-center justify-center gap-3 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl group"
                  >
                    <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send Email
                  </a>
                )}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* About Section - Takes up 2/3 on large screens */}
          <div className="lg:col-span-2">
            {mentor.bio && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-8"
              >
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">👋 About {mentor.name?.split(' ')[0]}</h2>
                </div>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-lg">
                    {mentor.bio}
                  </p>
                </div>
              </motion.div>
            )}
          </div>

          {/* Sidebar - Professional Details */}
          <div className="space-y-6">
            {/* Professional Details Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="bg-white rounded-xl shadow-lg border border-gray-100 p-6"
            >
              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900">🏢 Professional background</h3>
              </div>
              <div className="space-y-4">
                {mentor.currentRole && (
                  <div className="border-l-4 border-gray-200 pl-4">
                    <h4 className="text-sm font-semibold text-gray-500 mb-1 uppercase tracking-wide">Current Role</h4>
                    <p className="text-gray-900 font-semibold text-lg">{mentor.currentRole}</p>
                  </div>
                )}
                
                {mentor.currentSector && (
                  <div className="border-l-4 border-gray-200 pl-4">
                    <h4 className="text-sm font-semibold text-gray-500 mb-1 uppercase tracking-wide">Industry</h4>
                    <p className="text-gray-900 font-semibold">{mentor.currentSector}</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Best Suited To Support */}
            {((mentor.areasOfSpecialism && mentor.areasOfSpecialism.length > 0) || (mentor.bestAtAdviceOn && mentor.bestAtAdviceOn.length > 0)) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0 }}
                className="bg-white rounded-xl shadow-lg border border-gray-100 p-6"
              >
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Best suited to support</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(mentor.areasOfSpecialism && mentor.areasOfSpecialism.length > 0
                    ? mentor.areasOfSpecialism
                    : mentor.bestAtAdviceOn
                  ).map((area, index) => (
                    <span
                      key={index}
                      className="px-3 py-2 rounded-lg text-sm font-medium bg-[#02B491]/10 border border-[#02B491]/20 text-[#026b5c] hover:bg-[#02B491]/20 transition-colors"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>


      
    </div>
  );
};

export default MentorProfile;